import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Format inline text styles (bold, italic, code).
 */
export const formatInlineText = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Pattern matches: **bold**, *italic*, `code`, ~~strikethrough~~
    const regex = /(\*\*.*?\*\*|__.*?__|\*.*?\*|_.*?_|`.*?`|~~.*?~~)/g;
    const parts = text.split(regex);

    return parts.map((part, idx) => {
        if (!part) return null;

        // Bold (**text** or __text__)
        if (
            (part.startsWith('**') && part.endsWith('**') && part.length >= 4) ||
            (part.startsWith('__') && part.endsWith('__') && part.length >= 4)
        ) {
            return (
                <strong key={idx} className="font-bold text-slate-900">
                    {part.slice(2, -2)}
                </strong>
            );
        }

        // Italic (*text* or _text_)
        if (
            (part.startsWith('*') && part.endsWith('*') && part.length >= 2) ||
            (part.startsWith('_') && part.endsWith('_') && part.length >= 2)
        ) {
            return (
                <em key={idx} className="italic">
                    {part.slice(1, -1)}
                </em>
            );
        }

        // Code (`text`)
        if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
            return (
                <code key={idx} className="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-[11px] font-mono">
                    {part.slice(1, -1)}
                </code>
            );
        }

        // Strikethrough (~~text~~)
        if (part.startsWith('~~') && part.endsWith('~~') && part.length >= 4) {
            return (
                <del key={idx} className="line-through text-slate-400">
                    {part.slice(2, -2)}
                </del>
            );
        }

        return part;
    });
};

/**
 * Parses raw text into semantic blocks (paragraphs, lists, quotes, headings, spacing).
 */
export const parseNoteBlocks = (rawText) => {
    if (!rawText || typeof rawText !== 'string') return [];
    const lines = rawText.split('\n');
    const blocks = [];
    let currentBlock = null;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // Bullet lists (- item, * item, • item)
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ')) {
            const itemText = trimmed.replace(/^[-*•]\s+/, '');
            if (currentBlock && currentBlock.type === 'ul') {
                currentBlock.items.push(itemText);
            } else {
                if (currentBlock) blocks.push(currentBlock);
                currentBlock = { type: 'ul', items: [itemText] };
            }
        }
        // Numbered lists (1. item, 2) item)
        else if (/^\d+[\.\)]\s+/.test(trimmed)) {
            const itemText = trimmed.replace(/^\d+[\.\)]\s+/, '');
            if (currentBlock && currentBlock.type === 'ol') {
                currentBlock.items.push(itemText);
            } else {
                if (currentBlock) blocks.push(currentBlock);
                currentBlock = { type: 'ol', items: [itemText] };
            }
        }
        // Blockquotes (> quote)
        else if (trimmed.startsWith('> ') || trimmed === '>') {
            const quoteText = trimmed.replace(/^>\s*/, '');
            if (currentBlock && currentBlock.type === 'quote') {
                currentBlock.items.push(quoteText);
            } else {
                if (currentBlock) blocks.push(currentBlock);
                currentBlock = { type: 'quote', items: [quoteText] };
            }
        }
        // Headings (#, ##, ###)
        else if (trimmed.startsWith('### ')) {
            if (currentBlock) blocks.push(currentBlock);
            blocks.push({ type: 'h3', text: trimmed.slice(4) });
            currentBlock = null;
        } else if (trimmed.startsWith('## ')) {
            if (currentBlock) blocks.push(currentBlock);
            blocks.push({ type: 'h2', text: trimmed.slice(3) });
            currentBlock = null;
        } else if (trimmed.startsWith('# ')) {
            if (currentBlock) blocks.push(currentBlock);
            blocks.push({ type: 'h1', text: trimmed.slice(2) });
            currentBlock = null;
        }
        // Empty lines / paragraph separators
        else if (trimmed === '') {
            if (currentBlock) {
                blocks.push(currentBlock);
                currentBlock = null;
            }
            blocks.push({ type: 'empty' });
        }
        // Standard text lines
        else {
            if (currentBlock && currentBlock.type === 'p') {
                currentBlock.lines.push(line);
            } else {
                if (currentBlock) blocks.push(currentBlock);
                currentBlock = { type: 'p', lines: [line] };
            }
        }
    }

    if (currentBlock) blocks.push(currentBlock);
    return blocks;
};

const ALLOWED_TAGS = [
    'p', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    'ul', 'ol', 'li', 'blockquote', 'h2', 'h3', 'h4', 'br', 'span'
];

/**
 * FormattedNoteText component
 * Renders notes preserving paragraphs, line breaks, bullet lists, numbered lists,
 * bold, italic, quotes and headers with clean Tailwind typography.
 */
const FormattedNoteText = ({ text = '', className = '', fallback = 'Nessuna nota registrata' }) => {
    if (!text || (typeof text === 'string' && !text.trim())) {
        return <span className="italic text-gray-400">{fallback}</span>;
    }

    const str = String(text);

    // If text contains HTML tags from RichTextEditor, render sanitized HTML
    if (/<(p|strong|b|em|i|u|ul|ol|li|blockquote|h[1-6]|br)[^>]*>/i.test(str)) {
        const cleanHtml = DOMPurify.sanitize(str, { ALLOWED_TAGS });
        return (
            <div
                className={`prose prose-sm max-w-none text-inherit leading-relaxed prose-p:my-1 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-blockquote:my-1.5 prose-blockquote:border-l-3 prose-blockquote:border-indigo-400 prose-blockquote:bg-indigo-50/40 prose-blockquote:py-1.5 prose-blockquote:px-3 prose-blockquote:rounded-r-lg prose-blockquote:italic ${className}`}
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
            />
        );
    }

    const blocks = parseNoteBlocks(str);

    return (
        <div className={`space-y-1.5 leading-relaxed text-inherit ${className}`}>
            {blocks.map((block, bIdx) => {
                switch (block.type) {
                    case 'ul':
                        return (
                            <ul key={bIdx} className="list-disc pl-5 my-1.5 space-y-1">
                                {block.items.map((item, iIdx) => (
                                    <li key={iIdx} className="leading-snug">
                                        {formatInlineText(item)}
                                    </li>
                                ))}
                            </ul>
                        );
                    case 'ol':
                        return (
                            <ol key={bIdx} className="list-decimal pl-5 my-1.5 space-y-1">
                                {block.items.map((item, iIdx) => (
                                    <li key={iIdx} className="leading-snug">
                                        {formatInlineText(item)}
                                    </li>
                                ))}
                            </ol>
                        );
                    case 'quote':
                        return (
                            <blockquote
                                key={bIdx}
                                className="border-l-3 border-indigo-400/80 bg-indigo-50/40 pl-3 py-1.5 my-1.5 rounded-r-lg italic text-slate-700 whitespace-pre-wrap"
                            >
                                {block.items.map((qLine, qIdx) => (
                                    <p key={qIdx} className="leading-snug">
                                        {formatInlineText(qLine)}
                                    </p>
                                ))}
                            </blockquote>
                        );
                    case 'h1':
                        return (
                            <h4 key={bIdx} className="font-extrabold text-base text-slate-900 mt-2 mb-1">
                                {formatInlineText(block.text)}
                            </h4>
                        );
                    case 'h2':
                        return (
                            <h5 key={bIdx} className="font-bold text-sm text-slate-900 mt-1.5 mb-1">
                                {formatInlineText(block.text)}
                            </h5>
                        );
                    case 'h3':
                        return (
                            <h6 key={bIdx} className="font-bold text-xs uppercase tracking-wider text-slate-700 mt-1 mb-0.5">
                                {formatInlineText(block.text)}
                            </h6>
                        );
                    case 'empty':
                        return <div key={bIdx} className="h-1.5" />;
                    case 'p':
                    default:
                        return (
                            <p key={bIdx} className="whitespace-pre-wrap leading-relaxed">
                                {block.lines.map((line, lIdx) => (
                                    <React.Fragment key={lIdx}>
                                        {formatInlineText(line)}
                                        {lIdx < block.lines.length - 1 && '\n'}
                                    </React.Fragment>
                                ))}
                            </p>
                        );
                }
            })}
        </div>
    );
};

export default FormattedNoteText;
